/**
 * Notifications panel — list, mark as read.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPlayerNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../lib/playerApi";
import { Bell, ArrowLeft, CheckCheck, Circle } from "lucide-react";
import { PLAYER } from "../lib/testIds";

export default function NotificationsPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getPlayerNotifications();
      setItems(r.notifications || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (id) => {
    try {
      await markNotificationRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      /* ignore */
    }
  };

  const markAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <button
        data-testid={PLAYER.btnBack}
        onClick={() => navigate("/player/menu")}
        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Menu
      </button>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-orange-400" />
          <h2 className="text-xl font-bold">Notifications</h2>
        </div>
        {items.some((n) => !n.read) && (
          <button
            onClick={markAll}
            className="text-xs text-orange-300 hover:text-orange-200 flex items-center gap-1.5"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            Mark all read
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#11141b] border border-white/5 rounded-xl p-8 text-center text-slate-400">
          You&apos;re all caught up.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => !n.read && markRead(n.id)}
              className={`w-full text-left bg-[#11141b] hover:bg-[#171b25] border rounded-xl p-4 transition-colors ${
                n.read ? "border-white/5 opacity-70" : "border-orange-500/30"
              }`}
            >
              <div className="flex items-start gap-3">
                {!n.read && <Circle className="w-2 h-2 mt-2 fill-orange-400 text-orange-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{n.title}</div>
                  <div className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{n.message}</div>
                  <div className="text-[11px] text-slate-500 mt-2">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
